import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Lock, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { findOptionalModule } from '../lib/optionalModules';
import { toast } from 'sonner';

/**
 * Modal shown when a user tries to access an optional module that is not
 * enabled on their organization. No price is displayed — clicking "Activer
 * maintenant" simply turns the module ON via the team/enable-module API.
 */
const ModuleLockModal = ({ moduleKey, open, onOpenChange, onActivated }) => {
  const { enableModule, isOwnerOrAdmin } = useAuth();
  const [busy, setBusy] = useState(false);
  const meta = findOptionalModule(moduleKey);

  if (!meta) return null;

  const handleActivate = async () => {
    setBusy(true);
    try {
      await enableModule(moduleKey);
      toast.success(`Module « ${meta.label} » activé`);
      onOpenChange(false);
      if (onActivated) onActivated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'activer le module");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="module-lock-modal">
        <DialogHeader>
          <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center mb-2">
            <Lock className="w-6 h-6 text-amber-600" />
          </div>
          <DialogTitle className="font-outfit">Module non activé</DialogTitle>
          <DialogDescription>
            Le module <strong>« {meta.label} »</strong> n&apos;est pas encore activé sur votre compte.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700">
          {meta.description}
        </div>

        {!isOwnerOrAdmin() && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            Seul le propriétaire de votre organisation peut activer un nouveau module.
            Contactez-le pour qu&apos;il l&apos;active.
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="module-lock-cancel-btn">
            Plus tard
          </Button>
          <Button
            onClick={handleActivate}
            disabled={busy || !isOwnerOrAdmin()}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="module-lock-activate-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Activer maintenant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModuleLockModal;
